import type {
    AiServer,
    Article,
    ArticleListItem,
    ArticleMediaSummary,
    ArticleMediaUploadResponse,
    ArticlePreviewRequest,
    ArticlePreviewResponse,
    BulkImportPayload,
    BulkImportResult,
    CreateArticleRequest,
    SightingLexicalRule,
    JobAcceptedResponse,
    RecrawlRequest,
    RecrawlResponse,
    SoeImportResult,
    ConvergenceDriftSweepResult,
    PlatformStats,
    RebuildDenormalizedResult,
    SourceRateLimitView,
    AdminManifest,
    AuditEntry,
    Creator,
    CreatorDetail,
    CreatorPage,
    CreatorLifecycle,
    Platform,
    PlatformGameScope,
    Game,
    GameStatus,
    OAuthBindingPage,
    AuthorClaimPage,
    DeclinedAccountPage,
    ActiveRun,
    StopAllResponse,
    Work,
    Sighting,
    WorkSummary,
    PinRequest,
    ProbeUrlRequest,
    ProbeUrlResponse,
    ScheduleArticleRequest,
    Provider,
    FacetBundle,
    SuppressRequest,
    TranslationGlossaryEntry,
    TranslationSettings,
    TranslationOverview,
    TranslationResetResult,
    UpdateArticleRequest,
    UrlCacheWarmResult,
    TaskInfo,
    TriggerResponse,
    HttpCacheStats,
    FactoryResetResult,
    WipeDatabaseResult,
    PublicProfileDto,
    PublicCollectionSummary,
    AccountDto,
    AccountProfileUpdate,
    AccountVisibilityUpdate,
    AccountDeleteResponse,
    DiscoveryMode,
    GwtagWalletDto,
    ClaimDto,
    ClaimInboxItemDto,
    AuditPage,
    AuditListQuery,
    CalibrationView,
    CalibrationReplayView,
    CalibrationCompareView,
    ForceSplit,
    GoldenSetEntry,
    PendingHintRow,
    PipelineHealth,
    ScoutWipeResponse,
    RecentMergeRow,
    RecentRefreshRow,
    RecordGoldenRequest,
    RegressionResult,
    NotificationInbox,
    SourceCredentialView,
    SourceCredentialVerifyResult,
} from './types';
import { type ApiWork, toWork, toWorkSummary } from './adapter';

class ApiError extends Error {
    constructor(
        public status: number,
        public statusText: string,
        message: string,
    ) {
        super(message);
        this.name = 'ApiError';
    }
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        credentials: 'include',
        ...init,
        headers: {
            Accept: 'application/json',
            ...(init?.headers ?? {}),
        },
    });
    if (!res.ok) {
        throw new ApiError(res.status, res.statusText, `${res.status} ${res.statusText} on ${url}`);
    }
    return res.status === 204 ? (undefined as T) : ((await res.json()) as T);
}

/**
 * Koan's pagination contract: collection endpoints emit X-Page,
 * X-Page-Size, X-Total-Count, X-Total-Pages headers and a Link header
 * with rel="next"/"last". This helper surfaces those alongside the body
 * so callers can either page through results or build an unbounded
 * accumulator on top.
 */
export interface PagedResult<T> {
    items: T[];
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
}

async function fetchPaged<T>(url: string, init?: RequestInit): Promise<PagedResult<T>> {
    const res = await fetch(url, {
        credentials: 'include',
        ...init,
        headers: {
            Accept: 'application/json',
            ...(init?.headers ?? {}),
        },
    });
    if (!res.ok) {
        throw new ApiError(res.status, res.statusText, `${res.status} ${res.statusText} on ${url}`);
    }
    const items = res.status === 204 ? [] : ((await res.json()) as T[]);
    return {
        items,
        page: readIntHeader(res.headers, 'X-Page', 1),
        pageSize: readIntHeader(res.headers, 'X-Page-Size', items.length),
        totalCount: readIntHeader(res.headers, 'X-Total-Count', items.length),
        totalPages: readIntHeader(res.headers, 'X-Total-Pages', 1),
    };
}

function readIntHeader(headers: Headers, key: string, fallback: number): number {
    const raw = headers.get(key);
    if (!raw) return fallback;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
}

export { ApiError };

export interface WorkQuery {
    page?: number;
    size?: number;
    pageSize?: number;
    search?: string;
    sourceSlug?: string;
    kind?: string;
    /**
     * Koan JSON filter expression (see <c>JsonFilterBuilder</c> in
     * the Koan source). Top-level fields only - e.g.
     * <c>{"InstallUrlKind":"Mirror"}</c>, <c>{"Kind":{"$in":["Preset","Pack"]}}</c>,
     * <c>{"$or":[{"Name":"*term*"},{"Author":"*term*"}]}</c>.
     */
    filter?: string;
    /**
     * Sort grammar Koan parses against the entity (comma-separated;
     * <c>-</c> prefix = desc). Example: <c>-LastChangedAt</c>.
     */
    sort?: string;
    [key: string]: string | number | undefined;
}

function qs(params: Record<string, string | number | undefined>): string {
    const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
    if (entries.length === 0) return '';
    return (
        '?' +
        entries
            .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
            .join('&')
    );
}

/**
 * Apply a lifecycle Status change to an article through the unified gated endpoint: fetch the
 * current row, override the changed fields, and Upsert (POST) the whole article so the server's
 * BeforeUpsert hooks re-render and stamp the lifecycle fields. The former /api/admin/articles
 * publish/hide/unhide/schedule actions collapse to this single "set Status + Upsert" shape.
 */
async function upsertArticleStatus(
    id: string,
    change: { status: 'Published' | 'Scheduled' | 'Hidden'; publishAt?: string },
): Promise<Article> {
    const current = await fetchJson<Article>(`/api/articles/${encodeURIComponent(id)}`);
    return fetchJson<Article>('/api/articles', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...current, ...change }),
    });
}

export const api = {
    works: {
        list: async (query: WorkQuery = {}, set?: string, signal?: AbortSignal): Promise<PagedResult<WorkSummary>> => {
            const paged = await fetchPaged<ApiWork>(`/api/works${qs({ ...query, set })}`, { signal });
            return { ...paged, items: paged.items.map(toWorkSummary) };
        },
        listAllFull: async (query: WorkQuery = {}, set?: string): Promise<Work[]> => {
            const pageSize = (query.pageSize ?? query.size ?? 1000);
            const first = await fetchPaged<ApiWork>(
                `/api/works${qs({ ...query, view: 'full', page: 1, pageSize, set })}`,
            );
            const all: Work[] = first.items.map(toWork);
            for (let page = 2; page <= first.totalPages; page++) {
                const next = await fetchPaged<ApiWork>(
                    `/api/works${qs({ ...query, view: 'full', page, pageSize, set })}`,
                );
                for (const row of next.items) all.push(toWork(row));
            }
            return all;
        },
        get: async (id: string, set?: string): Promise<Work> => {
            const raw = await fetchJson<ApiWork>(
                `/api/works/${encodeURIComponent(id)}${qs({ set })}`,
            );
            return toWork(raw);
        },
        related: async (id: string, limit?: number, set?: string): Promise<WorkSummary[]> => {
            const rows = await fetchJson<ApiWork[]>(
                `/api/works/${encodeURIComponent(id)}/related${qs({ limit, set })}`,
            );
            return rows.map(toWorkSummary);
        },
        sightings: (workId: string) =>
            fetchJson<Sighting[]>(
                `/api/sightings/by-work/${encodeURIComponent(workId)}`,
            ),
    },
    providers: {
        list: () => fetchJson<Provider[]>('/api/scouts'),
    },
    /**
     * Public user profile + collection reads. Per ADR-0015 +
     * legacy ADR-0030 the user-facing surface lives at
     * <c>/u/{slug}</c>; the navbar avatar/name links here.
     * <c>getProfile</c> honours the visibility gate server-side (404
     * for Private to non-owners; 200 for Public/Unlisted).
     */
    users: {
        getProfile: (slug: string) =>
            fetchJson<PublicProfileDto>(`/api/u/${encodeURIComponent(slug)}`),
        listPublicCollections: (slug: string) =>
            fetchJson<PublicCollectionSummary[]>(
                `/api/u/${encodeURIComponent(slug)}/collections`,
            ),
    },
    /**
     * Self-service account surface (per ADR-0015 + legacy ADR-0028).
     * Every endpoint requires the session cookie; anonymous calls
     * return 401. The shell page at <c>/me/account</c> stitches these
     * together into a single curator-facing control panel.
     */
    me: {
        account: {
            get: () => fetchJson<AccountDto>('/api/me/account'),
            setSlug: (slug: string) =>
                fetchJson<AccountDto>('/api/me/account/slug', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ slug }),
                }),
            updateProfile: (body: AccountProfileUpdate) =>
                fetchJson<AccountDto>('/api/me/account/profile', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            setVisibility: (body: AccountVisibilityUpdate) =>
                fetchJson<AccountDto>('/api/me/account/visibility', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            setDiscoveryMode: (mode: DiscoveryMode) =>
                fetchJson<AccountDto>('/api/me/account/discovery', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ mode }),
                }),
        },
        gwtag: {
            get: () => fetchJson<GwtagWalletDto>('/api/me/gwtag'),
            set: (value: string) =>
                fetchJson<GwtagWalletDto>('/api/me/gwtag', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ value }),
                }),
            suggest: () =>
                fetchJson<{ value: string }>('/api/me/gwtag/suggest', {
                    method: 'POST',
                }),
            releaseReserved: (value: string) =>
                fetchJson<void>(
                    `/api/me/gwtag/reserved/${encodeURIComponent(value)}`,
                    { method: 'DELETE' },
                ),
        },
        claims: {
            list: () => fetchJson<ClaimDto[]>('/api/me/claims'),
            revoke: (id: string) =>
                fetchJson<void>(`/api/me/claims/${encodeURIComponent(id)}`, {
                    method: 'DELETE',
                }),
        },
        claimsInbox: {
            list: () => fetchJson<ClaimInboxItemDto[]>('/api/me/claims/inbox'),
            accept: (id: string) =>
                fetchJson<ClaimInboxItemDto>(
                    `/api/me/claims/inbox/${encodeURIComponent(id)}/accept`,
                    { method: 'POST' },
                ),
            decline: (id: string) =>
                fetchJson<ClaimInboxItemDto>(
                    `/api/me/claims/inbox/${encodeURIComponent(id)}/decline`,
                    { method: 'POST' },
                ),
            suppress: (id: string) =>
                fetchJson<ClaimInboxItemDto>(
                    `/api/me/claims/inbox/${encodeURIComponent(id)}/suppress`,
                    { method: 'POST' },
                ),
        },
        data: {
            /**
             * GDPR export streams one JSON file inline (per ADR-0015
             * Phase 3.2). The SPA navigates directly to this URL so the
             * browser handles Content-Disposition; no fetchJson needed.
             */
            exportUrl: '/api/me/account/export',
            delete: (confirm: string) =>
                fetchJson<AccountDeleteResponse>('/api/me/account/delete', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ confirm }),
                }),
        },
    },
    /**
     * Notification inbox (ADR-0028). Any authenticated user; anonymous
     * calls 401. The bell + alert-banner poll <c>getInbox</c>; the mutators
     * upsert the caller's per-user NotificationReceipt (read / dismiss) and
     * return 204. A dismiss only hides a Dismissible banner for the caller;
     * a sticky operational banner clears when its producer resolves it.
     */
    notifications: {
        getInbox: (take = 50) =>
            fetchJson<NotificationInbox>(`/api/notifications${qs({ take })}`),
        markRead: (id: string) =>
            fetchJson<void>(
                `/api/notifications/${encodeURIComponent(id)}/read`,
                { method: 'POST' },
            ),
        markAllRead: () =>
            fetchJson<void>('/api/notifications/read-all', { method: 'POST' }),
        dismiss: (id: string) =>
            fetchJson<void>(
                `/api/notifications/${encodeURIComponent(id)}/dismiss`,
                { method: 'POST' },
            ),
    },
    catalog: {
        /**
         * Full server-aggregated facet bundle for the catalog sidebar
         * (source, install, type, engine, game, access, locale). Loaded
         * once on `/emporium` mount so every chip list is stable
         * regardless of how far the infinite-scroll accumulator has
         * loaded - without this, chip lists grow as more pages stream
         * in and the visible filter universe drifts under the user.
         * Counts are visibility-scoped server-side.
         */
        facets: () => fetchJson<FacetBundle>('/api/catalog/facets'),
    },
    stats: {
        /**
         * Platform-wide counters snapshot (works, sightings, mirrors,
         * sources, etc). Recomputed by `PlatformStatsRollupTask`; the
         * endpoint serves the cached single-row snapshot so reads never
         * hot-path Mongo. Use this for landing-page chips and the admin
         * dashboard's at-a-glance counts instead of `?pageSize=1`
         * probes against paginated collections.
         */
        platform: () => fetchJson<PlatformStats>('/api/stats/platform'),
    },
    articles: {
        /**
         * Public list. Server returns Published-only, newest first,
         * projected to the slim <see cref="ArticleListItem"/> shape -
         * card data only; the rendered body / TOC / source hash are
         * dropped to keep the response small. <c>set</c> selects the
         * locale partition (ADR-0043). Detail endpoint still returns
         * the full <see cref="Article"/>.
         */
        list: (set?: string) => fetchJson<ArticleListItem[]>(`/api/articles${qs({ set })}`),
        /**
         * Public single-article lookup by id. The public route is
         * /articles/{id}/{slug} (E3): the id is the key, the slug decorative.
         * Backend returns 404 for non-public rows (Draft / Scheduled / Hidden)
         * and for unknown ids; the caller (typically the detail page) surfaces
         * that as a friendly "not found" view, distinct from a transport error.
         * <c>set</c> selects the locale partition.
         */
        getById: (id: string, set?: string) =>
            fetchJson<Article>(`/api/articles/${encodeURIComponent(id)}${qs({ set })}`),
    },
    admin: {
        operations: {
            /**
             * In-flight jobs per kind (JOBS-0003). The unified OperationsRun matrix was
             * removed; each job type is now its own persisted Koan set queried directly.
             */
            activeCrawls: () => fetchJson<ActiveRun[]>('/api/admin/crawls/active'),
            activeMirrors: () => fetchJson<ActiveRun[]>('/api/admin/mirrors/active'),
            /**
             * Cancel routing: the backend exposes two distinct endpoints (crawl vs
             * mirror refresh). Caller chooses `kind`. 404 means no such job of that kind.
             */
            cancel: (jobId: string, kind: 'crawl' | 'mirror'): Promise<void> => {
                const path =
                    kind === 'crawl'
                        ? `/api/admin/crawls/${encodeURIComponent(jobId)}/cancel`
                        : `/api/admin/mirrors/refreshes/${encodeURIComponent(jobId)}/cancel`;
                return fetchJson<void>(path, { method: 'POST' });
            },
            stopAllCrawls: () =>
                fetchJson<StopAllResponse>('/api/admin/crawls/stop-all', { method: 'POST' }),
            stopAllMirrors: () =>
                fetchJson<StopAllResponse>('/api/admin/mirrors/stop-all', { method: 'POST' }),
        },
        scouts: {
            triggerCrawl: (scoutId: string) =>
                fetchJson<JobAcceptedResponse>(
                    `/api/admin/scouts/${encodeURIComponent(scoutId)}/crawl`,
                    { method: 'POST' },
                ),
            /**
             * Flip Scout.IsEnabled = true (ADR-0015 admin polish).
             * The new legacy-* scouts (arca.live, Glamour Dresser,
             * Patreon) ship DefaultEnabled = false; the Sources admin
             * page calls this when the operator toggles them on.
             * Idempotent; audit log records the transition.
             */
            enable: (scoutId: string) =>
                fetchJson<Provider>(
                    `/api/admin/scouts/${encodeURIComponent(scoutId)}/enable`,
                    { method: 'POST' },
                ),
            /** Flip Scout.IsEnabled = false. Idempotent. */
            disable: (scoutId: string) =>
                fetchJson<Provider>(
                    `/api/admin/scouts/${encodeURIComponent(scoutId)}/disable`,
                    { method: 'POST' },
                ),
            /**
             * Fire Scout.Stage.Discover for every enabled scout in one
             * request. Per ADR-0015 the bulk "re-crawl the whole catalog"
             * hammer; each fire writes its own audit row.
             */
            triggerAllCrawls: () =>
                fetchJson<{
                    enabledCount: number;
                    triggered: JobAcceptedResponse[];
                }>('/api/admin/scouts/crawl-all', { method: 'POST' }),
            /**
             * Erase every piece of state this scout owns: Sighting
             * rows, lone-scout Works (with aliases + signal index),
             * outbound HTTP cache (by Source brand), filesystem rejection
             * sidecars, and any non-terminal jobs touching those sightings.
             * Optionally reprime with a fresh discovery crawl.
             *
             * <p>Use when the negative cache has built up wrong entries,
             * the scout's page schema changed, or you want a clean
             * slate to validate a new extractor against. Per the
             * greenfield/wipe-and-recreate policy, this is the per-source
             * equivalent of the global DB wipe.</p>
             */
            wipe: (scoutId: string, opts: { reprime: boolean }) =>
                fetchJson<ScoutWipeResponse>(
                    `/api/admin/scouts/${encodeURIComponent(scoutId)}/wipe?reprime=${opts.reprime}`,
                    { method: 'POST' },
                ),
        },
        /**
         * Generic IScheduledTask console (ADR-0015 Phase 2.1). Lists
         * every registered task with last-run metadata and triggers
         * any one of them on demand. The natural home for sweeps,
         * backfills, seeders the Maintenance panel doesn't surface
         * individually.
         */
        tasks: {
            list: () =>
                fetchJson<TaskInfo[]>('/api/admin/tasks'),
            trigger: (taskId: string) =>
                fetchJson<TriggerResponse>(
                    `/api/admin/tasks/${encodeURIComponent(taskId)}/trigger`,
                    { method: 'POST' },
                ),
        },
        /**
         * Audit log reads (ADR-0015 Phase 2.4). Every fine-grained
         * admin / user action AuditWriter persists is queryable here
         * by action / target / actor / severity / time range.
         */
        audit: {
            list: (query: AuditListQuery = {}) => {
                const qs = new URLSearchParams();
                if (query.action) qs.set('action', query.action);
                if (query.targetType) qs.set('targetType', query.targetType);
                if (query.targetId) qs.set('targetId', query.targetId);
                if (query.actorUserId) qs.set('actorUserId', query.actorUserId);
                if (query.severity) qs.set('severity', query.severity);
                if (query.since) qs.set('since', query.since);
                if (query.until) qs.set('until', query.until);
                if (query.correlation) qs.set('correlation', query.correlation);
                if (query.page) qs.set('page', String(query.page));
                if (query.pageSize) qs.set('pageSize', String(query.pageSize));
                const tail = qs.toString();
                return fetchJson<AuditPage>(
                    `/api/admin/audit${tail ? `?${tail}` : ''}`,
                );
            },
            /**
             * Full single-entry detail. Mirrors what list() returns per row
             * but the dedicated GET is here so the detail drawer can refetch
             * cleanly without re-paging the list.
             */
            get: (id: string) =>
                fetchJson<AuditEntry>(`/api/admin/audit/${encodeURIComponent(id)}`),
        },
        /**
         * Source-rate-limit gate (ADR-0021). Each row blocks every outbound
         * request to the brand until BlockedUntil passes. Operators clear a
         * row when a 429/1015 false-triggered or the upstream has explicitly
         * recovered.
         */
        sourceRateLimits: {
            list: () =>
                fetchJson<SourceRateLimitView[]>('/api/admin/source-rate-limits'),
            clear: (source: string) =>
                fetchJson<void>(
                    `/api/admin/source-rate-limits/${encodeURIComponent(source)}`,
                    { method: 'DELETE' },
                ),
        },
        /**
         * Runtime source-credential vault (ADR-0028). Write-only discipline:
         * the GET projection never returns the token or ciphertext, only the
         * non-secret metadata. PUT rotates (an empty value is rejected 400 and
         * never wipes the stored token); DELETE reverts to env/anonymous;
         * <c>verify</c> runs a non-persisting probe against the entered-or-
         * stored token for instant green/red without waiting for the next crawl.
         */
        sourceCredentials: {
            list: () =>
                fetchJson<SourceCredentialView[]>('/api/admin/source-credentials'),
            set: (source: string, token: string) =>
                fetchJson<void>(
                    `/api/admin/source-credentials/${encodeURIComponent(source)}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ token }),
                    },
                ),
            clear: (source: string) =>
                fetchJson<void>(
                    `/api/admin/source-credentials/${encodeURIComponent(source)}`,
                    { method: 'DELETE' },
                ),
            verify: (source: string, token?: string) =>
                fetchJson<SourceCredentialVerifyResult>(
                    `/api/admin/source-credentials/${encodeURIComponent(source)}/verify`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({ token }),
                    },
                ),
        },
        /**
         * Build / deploy metadata snapshot. Drives the operator-facing
         * "About" page (version, scheduled-task registry, source host).
         */
        manifest: () => fetchJson<AdminManifest>('/api/admin/manifest'),
        /**
         * Creator admin surface per ADR-0024 Tranche D.1. Paged browse
         * + detail (Creator + CreatorPlatformBinding rows) + Hide/Unhide
         * lifecycle. Force-merge + force-unbind land in a follow-up
         * tranche once the CreatorConverge orchestrator stabilises.
         */
        creators: {
            list: (query: { page?: number; pageSize?: number; q?: string; lifecycle?: CreatorLifecycle } = {}) => {
                const qs = new URLSearchParams();
                if (query.page) qs.set('page', String(query.page));
                if (query.pageSize) qs.set('pageSize', String(query.pageSize));
                if (query.q) qs.set('q', query.q);
                if (query.lifecycle) qs.set('lifecycle', query.lifecycle);
                const tail = qs.toString();
                return fetchJson<CreatorPage>(
                    `/api/admin/creators${tail ? `?${tail}` : ''}`,
                );
            },
            get: (id: string) =>
                fetchJson<CreatorDetail>(`/api/admin/creators/${encodeURIComponent(id)}`),
            hide: (id: string) =>
                fetchJson<Creator>(
                    `/api/admin/creators/${encodeURIComponent(id)}/hide`,
                    { method: 'POST' },
                ),
            unhide: (id: string) =>
                fetchJson<Creator>(
                    `/api/admin/creators/${encodeURIComponent(id)}/hide`,
                    { method: 'DELETE' },
                ),
            /**
             * Collapse two Creators into one. Bindings reassign to the
             * survivor, aliases repoint, a RetiredCreatorId alias keeps
             * the loser id resolvable. Loser Creator row is hard-deleted.
             */
            forceMerge: (loserId: string, body: { survivorId: string }) =>
                fetchJson<{
                    retiredId: string;
                    survivorId: string;
                    bindingsReassigned: number;
                }>(
                    `/api/admin/creators/${encodeURIComponent(loserId)}/force-merge`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                    },
                ),
            /**
             * Detach a single CreatorPlatformBinding. Removes the binding
             * row + its (PlatformHandle) CreatorAlias. Sightings that
             * referenced the binding will re-resolve via the convergence
             * engine on next Sighting.Evaluate touch.
             */
            forceUnbind: (bindingId: string) =>
                fetchJson<void>(
                    `/api/admin/creators/bindings/${encodeURIComponent(bindingId)}`,
                    { method: 'DELETE' },
                ),
        },
        /**
         * Platform registry + operator metadata (ADR-0024 §4.1 satellite
         * promotion). The identity fields come from the code-constant
         * Platforms registry; the metadata fields come from the
         * PlatformMetadata entity overlay. Upsert/clear operates on the
         * metadata side.
         */
        platforms: {
            list: () => fetchJson<Platform[]>('/api/admin/platforms'),
            get: (id: string) =>
                fetchJson<Platform>(`/api/admin/platforms/${encodeURIComponent(id)}`),
            updateMetadata: (id: string, body: {
                tosCheckedAt?: string | null;
                clearTosCheckedAt?: boolean;
                contactEmail?: string | null;
                notes?: string | null;
                updatedBy?: string | null;
            }) =>
                fetchJson<Platform>(
                    `/api/admin/platforms/${encodeURIComponent(id)}/metadata`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                    },
                ),
            clearMetadata: (id: string) =>
                fetchJson<void>(
                    `/api/admin/platforms/${encodeURIComponent(id)}/metadata`,
                    { method: 'DELETE' },
                ),
        },
        /**
         * Platform × Game scope CRUD per ADR-0024 Tranche D.3. The
         * composite id is `{platformId}#{gameSlug}` (lowercase). Use
         * upsert to create a new scope or rewrite an existing one; the
         * route id MUST match the composite the body implies.
         */
        platformGameScopes: {
            list: (query: { platformId?: string; gameSlug?: string } = {}) => {
                const qs = new URLSearchParams();
                if (query.platformId) qs.set('platformId', query.platformId);
                if (query.gameSlug) qs.set('gameSlug', query.gameSlug);
                const tail = qs.toString();
                return fetchJson<PlatformGameScope[]>(
                    `/api/admin/platform-game-scopes${tail ? `?${tail}` : ''}`,
                );
            },
            get: (id: string) =>
                fetchJson<PlatformGameScope>(
                    `/api/admin/platform-game-scopes/${encodeURIComponent(id)}`,
                ),
            upsert: (
                id: string,
                body: {
                    platformId: string;
                    gameSlug: string;
                    platformRefs?: Record<string, string>;
                    moderationPolicy?: Record<string, string>;
                    isEnabled?: boolean;
                },
            ) =>
                fetchJson<PlatformGameScope>(
                    `/api/admin/platform-game-scopes/${encodeURIComponent(id)}`,
                    {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                    },
                ),
            delete: (id: string) =>
                fetchJson<void>(
                    `/api/admin/platform-game-scopes/${encodeURIComponent(id)}`,
                    { method: 'DELETE' },
                ),
        },
        /**
         * Game taxonomy CRUD per ADR-0024 Tranche D.4. Game is the
         * single source of truth for the game axis (slug + detection +
         * display); changes here re-project the generated
         * tag-game-{slug} lexical rules on the next startup or via the
         * GameRuleProjectionService trigger.
         */
        games: {
            list: (query: { status?: GameStatus; q?: string } = {}) => {
                const qs = new URLSearchParams();
                if (query.status) qs.set('status', query.status);
                if (query.q) qs.set('q', query.q);
                const tail = qs.toString();
                return fetchJson<Game[]>(
                    `/api/admin/games${tail ? `?${tail}` : ''}`,
                );
            },
            get: (id: string) =>
                fetchJson<Game>(`/api/admin/games/${encodeURIComponent(id)}`),
            upsert: (
                id: string,
                body: {
                    slug: string;
                    displayName: string;
                    status?: GameStatus;
                    addedBy?: string;
                    detectionAliases?: string[];
                },
            ) =>
                fetchJson<Game>(`/api/admin/games/${encodeURIComponent(id)}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            delete: (id: string) =>
                fetchJson<void>(`/api/admin/games/${encodeURIComponent(id)}`, {
                    method: 'DELETE',
                }),
        },
        /**
         * OAuth binding forensic admin per ADR-0024 Tranche D.5. Paged
         * list with filter by scheme / user. Force-unbind sets
         * OAuthBinding.DeletedAt = now (soft-delete); the AccountLink
         * lookup excludes soft-deleted rows, so a re-signin during the
         * grace creates a fresh user.
         */
        oauthBindings: {
            list: (query: {
                page?: number;
                pageSize?: number;
                scheme?: string;
                userId?: string;
                includeDeleted?: boolean;
            } = {}) => {
                const qs = new URLSearchParams();
                if (query.page) qs.set('page', String(query.page));
                if (query.pageSize) qs.set('pageSize', String(query.pageSize));
                if (query.scheme) qs.set('scheme', query.scheme);
                if (query.userId) qs.set('userId', query.userId);
                if (query.includeDeleted) qs.set('includeDeleted', 'true');
                const tail = qs.toString();
                return fetchJson<OAuthBindingPage>(
                    `/api/admin/oauth-bindings${tail ? `?${tail}` : ''}`,
                );
            },
            unbind: (id: string) =>
                fetchJson<void>(`/api/admin/oauth-bindings/${encodeURIComponent(id)}`, {
                    method: 'DELETE',
                }),
        },
        /**
         * AuthorClaim admin queue per ADR-0024 Tranche D.7. Paged list
         * with filter by user / provider slug / revoked status. Admin
         * revoke is a hard-revoke (sets RevokedAt = now) - distinct from
         * the self-service revoke on /api/me/claims.
         */
        authorClaims: {
            list: (query: {
                page?: number;
                pageSize?: number;
                userId?: string;
                providerSlug?: string;
                includeRevoked?: boolean;
            } = {}) => {
                const qs = new URLSearchParams();
                if (query.page) qs.set('page', String(query.page));
                if (query.pageSize) qs.set('pageSize', String(query.pageSize));
                if (query.userId) qs.set('userId', query.userId);
                if (query.providerSlug) qs.set('providerSlug', query.providerSlug);
                if (query.includeRevoked) qs.set('includeRevoked', 'true');
                const tail = qs.toString();
                return fetchJson<AuthorClaimPage>(
                    `/api/admin/claims${tail ? `?${tail}` : ''}`,
                );
            },
            revoke: (id: string) =>
                fetchJson<void>(`/api/admin/claims/${encodeURIComponent(id)}`, {
                    method: 'DELETE',
                }),
        },
        /**
         * DeclinedAccount admin per ADR-0024 Tranche D.7. The harassment-
         * guard ledger; rows here silence further claim suggestions for
         * a (UserId, ProviderSlug, ProviderHandle) tuple. Admin can
         * clear a row when a false-positive landed.
         */
        declinedAccounts: {
            list: (query: { page?: number; pageSize?: number; userId?: string } = {}) => {
                const qs = new URLSearchParams();
                if (query.page) qs.set('page', String(query.page));
                if (query.pageSize) qs.set('pageSize', String(query.pageSize));
                if (query.userId) qs.set('userId', query.userId);
                const tail = qs.toString();
                return fetchJson<DeclinedAccountPage>(
                    `/api/admin/declined-accounts${tail ? `?${tail}` : ''}`,
                );
            },
            clear: (id: string) =>
                fetchJson<void>(`/api/admin/declined-accounts/${encodeURIComponent(id)}`, {
                    method: 'DELETE',
                }),
        },
        /**
         * Force a global denormalizer re-run by submitting Sighting.Stage.Converge
         * for every Evaluated sighting. The orchestrator's converge lane
         * (MaxConcurrency=1, [JobIdempotent]) serialises the actual work
         * against any in-flight pipeline cycle. Use this after a denormalizer
         * code change to backfill historical Works without a per-row
         * re-trigger.
         */
        rebuildDenormalized: () =>
            fetchJson<RebuildDenormalizedResult>(
                '/api/admin/works/rebuild-denormalized',
                { method: 'POST' },
            ),
        /**
         * Re-run the SoE curated-preset bulk-import against the local
         * snapshot (FilesystemSoeCorpusCache resolves it on demand). The
         * importer is idempotent — re-running a healthy catalog produces
         * Unchanged counts only — so this is safe to fire when SoE corpus
         * shape changes upstream or after a partial earlier run.
         */
        soePresetImport: () =>
            fetchJson<BulkImportResult>('/api/admin/presets/soe-import', {
                method: 'POST',
            }),
        /**
         * Outbound HTTP cache management. Per-source purge for
         * surgical clears; nuke-all for the "something is really wrong,
         * start fresh" case.
         */
        httpCache: {
            stats: () => fetchJson<HttpCacheStats>('/api/admin/http-cache/stats'),
            purgeAll: () =>
                fetchJson<number>('/api/admin/http-cache', { method: 'DELETE' }),
            purgeBySource: (source: string) =>
                fetchJson<number>(
                    `/api/admin/http-cache/by-source/${encodeURIComponent(source)}`,
                    { method: 'DELETE' },
                ),
            /**
             * Replay one captured URL from the filesystem buffer.
             * Returns the raw body; the caller is responsible for
             * dispatching to a renderer if it wants to display the
             * payload (mainly a curator diagnostic).
             */
            captureReplay: (url: string) =>
                fetch(
                    `/api/admin/http-cache/capture-replay?url=${encodeURIComponent(url)}`,
                    { credentials: 'include' },
                ),
        },
        /**
         * Catalog factory reset per ADR-0015 Phase 8. Wipes all
         * sightings/works/mirrors/likes/collections + the HTTP
         * cache + rate gate; preserves accounts, audit trail, and
         * the filesystem capture buffer. Body MUST include
         * <c>{"confirm":"WIPE"}</c>; 409 when a reset is in flight.
         */
        factoryReset: (confirm: string) =>
            fetchJson<FactoryResetResult>('/api/admin/factory-reset', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirm }),
            }),
        /**
         * Nuclear option: drop the entire MongoDB database and
         * gracefully stop the host so the container restart policy
         * brings the app back with empty collections (Koan
         * auto-registrars re-create indexes/TTLs on the next boot).
         * Body MUST include <c>{"confirm":"WIPE-EVERYTHING"}</c>.
         * Returns 202 with a short-lived response BEFORE the actual
         * drop fires; the client should expect the server to become
         * unreachable for ~30s while the container restarts.
         */
        wipeDatabase: (confirm: string) =>
            fetchJson<WipeDatabaseResult>('/api/admin/wipe-database', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ confirm }),
            }),
        maintenance: {
            /**
             * Unified recrawl. Fires one CrawlJob per provider in scope;
             * each job carries the chosen mode (Discover vs ReExtract)
             * and the cache / rejection bypass flags. Supersedes the
             * old per-task backfill / re-extract endpoints - same
             * underlying machinery, one verb. See the request body
             * fields on RecrawlRequest; reasonable defaults
             * (bypass-everything, mode=Discover, fill-on-null) apply
             * when omitted.
             */
            recrawl: (body: RecrawlRequest) =>
                fetchJson<RecrawlResponse>(
                    '/api/admin/recrawl',
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                    },
                ),
            /**
             * Re-trigger the Sights-of-Eorzea article bootstrap. Falls
             * back to the local filesystem capture buffer if SoE is
             * offline, so the corpus rebuilds even when the upstream
             * is unreachable. Idempotent.
             */
            soeArticleImport: () =>
                fetchJson<SoeImportResult>(
                    '/api/admin/articles/soe-import',
                    { method: 'POST' },
                ),
            /**
             * Trigger the convergence-drift sweep on demand. Walks every
             * multi-sighting Work, re-scores its members against the
             * current scorer, and auto-detaches anyone whose score
             * falls below the merge threshold. Same code path as the
             * scheduled task. Returns per-detachment counters; the audit
             * log carries the full per-row trail.
             */
            convergenceDriftSweep: () =>
                fetchJson<ConvergenceDriftSweepResult>(
                    '/api/admin/works/drift-sweep',
                    { method: 'POST' },
                ),
            /**
             * Walk every Active sighting and pre-warm the URL content
             * cache (the file-only blob store that survives DB wipes).
             * After a successful warm, the next DB wipe doesn't lose
             * any preview bytes - they're already on local disk.
             */
            warmUrlCache: () =>
                fetchJson<UrlCacheWarmResult>(
                    '/api/admin/url-cache/warm',
                    { method: 'POST' },
                ),
        },
        works: {
            suppress: async (id: string, body: SuppressRequest): Promise<Work> => {
                const raw = await fetchJson<ApiWork>(
                    `/api/admin/works/${encodeURIComponent(id)}/suppress`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                    },
                );
                return toWork(raw);
            },
            unsuppress: async (id: string): Promise<Work> => {
                const raw = await fetchJson<ApiWork>(
                    `/api/admin/works/${encodeURIComponent(id)}/suppress`,
                    { method: 'DELETE' },
                );
                return toWork(raw);
            },
            pin: async (id: string, body: PinRequest): Promise<Work> => {
                const raw = await fetchJson<ApiWork>(
                    `/api/admin/works/${encodeURIComponent(id)}/pin`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                    },
                );
                return toWork(raw);
            },
            unpin: async (id: string): Promise<Work> => {
                const raw = await fetchJson<ApiWork>(
                    `/api/admin/works/${encodeURIComponent(id)}/pin`,
                    { method: 'DELETE' },
                );
                return toWork(raw);
            },
            /**
             * Operator-driven split: peel a chosen subset of the Work's
             * Sightings off into a freshly-minted Work. Returns the two
             * resulting ids plus pairwise force-split constraints written
             * (so the next convergence pass doesn't undo the split).
             * Empty `sightingIds` is rejected server-side.
             */
            split: (
                workId: string,
                body: { sightingIds: string[]; writeForceSplits?: boolean; note?: string | null },
            ) =>
                fetchJson<{
                    originalWorkId: string;
                    newWorkId: string;
                    sightingsMoved: number;
                    sightingsKept: number;
                    forceSplitConstraintsWritten: number;
                }>(`/api/admin/works/${encodeURIComponent(workId)}/split`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
        },
        /**
         * Pipeline-health snapshot per ADR-0022 §Flow - aggregate counts
         * for every observable pipeline state (Sighting stages, Work
         * statuses, override directives, pending operator-attention items).
         * Drives the admin Dashboard pipeline-funnel surface.
         */
        pipeline: {
            health: () => fetchJson<PipelineHealth>('/api/admin/pipeline/health'),
        },
        /**
         * Operator-override surface for ADR-0022 Phase 3 convergence.
         * Force-merge collapses two Works immediately; force-split
         * locks a pair open so future scored passes won't auto-merge them
         * even if signals overlap. Read endpoints surface convergence
         * activity the operator otherwise can't see (the converge job
         * runs silently on a single-concurrency lane).
         */
        convergence: {
            forceMerge: (loserWorkId: string, body: { targetWorkId: string }) =>
                fetchJson<{ retiredId: string; survivorId: string; sightingsReassigned: number }>(
                    `/api/admin/works/${encodeURIComponent(loserWorkId)}/force-merge`,
                    {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(body),
                    },
                ),
            forceSplit: (body: { workIdA: string; workIdB: string; note?: string | null }) =>
                fetchJson<ForceSplit>('/api/admin/works/force-split', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            releaseForceSplit: (workIdA: string, workIdB: string) =>
                fetchJson<void>(
                    `/api/admin/works/force-split/${encodeURIComponent(workIdA)}/${encodeURIComponent(workIdB)}`,
                    { method: 'DELETE' },
                ),
            recentMerges: (limit = 50) =>
                fetchJson<RecentMergeRow[]>(
                    `/api/admin/works/recent-merges?limit=${limit}`,
                ),
            pendingHints: () =>
                fetchJson<PendingHintRow[]>('/api/admin/works/pending-hints'),
            forceSplits: () =>
                fetchJson<ForceSplit[]>('/api/admin/works/force-splits'),
            refreshRecent: (limit = 50) =>
                fetchJson<RecentRefreshRow[]>(
                    `/api/admin/works/refresh-recent?limit=${limit}`,
                ),
        },
        /**
         * Calibration harness (ADR-0022 Phase 3.1 / 3.2). Sighting
         * inspection + side-by-side compare + golden-set CRUD + regression
         * replay. The data shape is "operator-facing diagnostic" - the
         * SPA renders these directly rather than mapping into something
         * smaller.
         */
        calibration: {
            getSighting: (sightingId: string) =>
                fetchJson<CalibrationView>(
                    `/api/admin/calibration/sightings/${encodeURIComponent(sightingId)}`,
                ),
            replaySighting: (sightingId: string) =>
                fetchJson<CalibrationReplayView>(
                    `/api/admin/calibration/sightings/${encodeURIComponent(sightingId)}/replay`,
                    { method: 'POST' },
                ),
            compare: (a: string, b: string, threshold?: number) => {
                const params = new URLSearchParams({ a, b });
                if (threshold !== undefined) params.set('threshold', String(threshold));
                return fetchJson<CalibrationCompareView>(
                    `/api/admin/calibration/compare?${params.toString()}`,
                );
            },
            recordGolden: (body: RecordGoldenRequest) =>
                fetchJson<GoldenSetEntry>('/api/admin/calibration/golden', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            listGolden: () =>
                fetchJson<GoldenSetEntry[]>('/api/admin/calibration/golden'),
            deleteGolden: (a: string, b: string) =>
                fetchJson<void>(
                    `/api/admin/calibration/golden/${encodeURIComponent(a)}/${encodeURIComponent(b)}`,
                    { method: 'DELETE' },
                ),
            runRegression: (threshold?: number) => {
                const tail = threshold === undefined ? '' : `?threshold=${threshold}`;
                return fetchJson<RegressionResult>(
                    `/api/admin/calibration/regression${tail}`,
                    { method: 'POST' },
                );
            },
        },
        import: {
            post: (payload: BulkImportPayload) =>
                fetchJson<BulkImportResult>('/api/admin/import', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(payload),
                }),
        },
        // The Article surface is the unified, gated /api/articles controller (the former
        // /api/admin/articles is gone). These admin helpers map the editor's intents onto it:
        // create is a plain Upsert (POST) of a new draft; update and the lifecycle transitions
        // GET the current row, apply the change, and Upsert the WHOLE article - Upsert is
        // full-replace, and re-sending the whole row fires the server's BeforeUpsert hooks that
        // re-render the body and stamp the lifecycle fields (PublishedAt / PublishAt /
        // slug-uniqueness all live in ArticleLifecycleSubscription now).
        articles: {
            // Admin authoring table: every status (admin gate) + full rows (?view=full bypasses
            // the card projection so Status / timestamps are present).
            list: () => fetchJson<Article[]>('/api/articles?view=full'),
            get: (id: string) =>
                fetchJson<Article>(`/api/articles/${encodeURIComponent(id)}`),
            create: (body: CreateArticleRequest) =>
                fetchJson<Article>('/api/articles', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            update: async (id: string, body: UpdateArticleRequest): Promise<Article> => {
                const current = await fetchJson<Article>(
                    `/api/articles/${encodeURIComponent(id)}`,
                );
                return fetchJson<Article>('/api/articles', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ ...current, ...body }),
                });
            },
            publish: (id: string) => upsertArticleStatus(id, { status: 'Published' }),
            schedule: (id: string, body: ScheduleArticleRequest) =>
                upsertArticleStatus(id, { status: 'Scheduled', publishAt: body.publishAt }),
            hide: (id: string) => upsertArticleStatus(id, { status: 'Hidden' }),
            unhide: (id: string) => upsertArticleStatus(id, { status: 'Published' }),
            delete: (id: string) =>
                fetchJson<void>(`/api/articles/${encodeURIComponent(id)}`, {
                    method: 'DELETE',
                }),
            preview: (body: ArticlePreviewRequest, signal?: AbortSignal) =>
                fetchJson<ArticlePreviewResponse>('/api/articles/preview', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                    signal,
                }),
        },
        translationSettings: {
            /** Returns the singleton; synthesises a default-on row when none exists yet. */
            get: () => fetchJson<TranslationSettings>('/api/admin/translation-settings'),
            /**
             * Upsert. Server forces the singleton id, stamps the
             * transition timestamps when Enabled flips, and calls
             * ITranslationGate.Invalidate so the worker picks up the
             * change on its next tick.
             */
            update: (body: TranslationSettings) =>
                fetchJson<TranslationSettings>('/api/admin/translation-settings', {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
        },
        translationOverview: {
            /** ADR-0030 routing snapshot: group -> model + live serveability + backlog. */
            get: () => fetchJson<TranslationOverview>('/api/admin/translation/overview'),
        },
        /**
         * ADR-0031 hard reset: re-populate every locale partition from the
         * current source (discarding stale machine translations + the
         * structural-reject flag), enqueue a fresh translation job for
         * each, then enable the framework gate and invalidate it so the
         * worker starts draining. Heavy + idempotent; use after a glossary
         * / prompt / pipeline change to re-translate the whole corpus.
         */
        translationReset: () =>
            fetchJson<TranslationResetResult>('/api/admin/translation/reset', {
                method: 'POST',
            }),
        glossary: {
            list: () => fetchJson<TranslationGlossaryEntry[]>('/api/admin/translation-glossary'),
            get: (id: string) =>
                fetchJson<TranslationGlossaryEntry>(`/api/admin/translation-glossary/${encodeURIComponent(id)}`),
            create: (body: Partial<TranslationGlossaryEntry>) =>
                fetchJson<TranslationGlossaryEntry>('/api/admin/translation-glossary', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            update: (id: string, body: TranslationGlossaryEntry) =>
                fetchJson<TranslationGlossaryEntry>(`/api/admin/translation-glossary/${encodeURIComponent(id)}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            delete: (id: string) =>
                fetchJson<void>(`/api/admin/translation-glossary/${encodeURIComponent(id)}`, {
                    method: 'DELETE',
                }),
        },
        sightingLexicalRules: {
            list: () => fetchJson<SightingLexicalRule[]>('/api/admin/sighting-lexical-rules'),
            get: (id: string) =>
                fetchJson<SightingLexicalRule>(`/api/admin/sighting-lexical-rules/${encodeURIComponent(id)}`),
            create: (body: Partial<SightingLexicalRule>) =>
                fetchJson<SightingLexicalRule>('/api/admin/sighting-lexical-rules', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            update: (id: string, body: Partial<SightingLexicalRule>) =>
                fetchJson<SightingLexicalRule>(`/api/admin/sighting-lexical-rules/${encodeURIComponent(id)}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            delete: (id: string) =>
                fetchJson<void>(`/api/admin/sighting-lexical-rules/${encodeURIComponent(id)}`, {
                    method: 'DELETE',
                }),
        },
        aiServers: {
            list: () => fetchJson<AiServer[]>('/api/admin/ai-servers'),
            get: (id: string) =>
                fetchJson<AiServer>(`/api/admin/ai-servers/${encodeURIComponent(id)}`),
            create: (body: Partial<AiServer>) =>
                fetchJson<AiServer>('/api/admin/ai-servers', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            update: (id: string, body: AiServer) =>
                fetchJson<AiServer>(`/api/admin/ai-servers/${encodeURIComponent(id)}`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
            delete: (id: string) =>
                fetchJson<void>(`/api/admin/ai-servers/${encodeURIComponent(id)}`, {
                    method: 'DELETE',
                }),
            probeUrl: (body: ProbeUrlRequest) =>
                fetchJson<ProbeUrlResponse>('/api/admin/ai-servers/probe-url', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(body),
                }),
        },
        articleMedia: {
            list: () => fetchJson<ArticleMediaSummary[]>('/api/admin/article-media'),
            /**
             * Upload a single image file. Returns the storage key
             * (SHA-256 of the bytes) the editor uses to reference the
             * image in markdown (`![](media:HASH)`) or as a cover
             * (the article's `coverMediaId`). Idempotent: re-uploading
             * the same bytes returns the existing key.
             */
            upload: async (file: File): Promise<ArticleMediaUploadResponse> => {
                const form = new FormData();
                form.append('file', file);
                const res = await fetch('/api/admin/article-media', {
                    method: 'POST',
                    credentials: 'include',
                    body: form,
                });
                if (!res.ok) {
                    throw new ApiError(
                        res.status,
                        res.statusText,
                        `${res.status} ${res.statusText} on /api/admin/article-media`,
                    );
                }
                return (await res.json()) as ArticleMediaUploadResponse;
            },
            delete: (key: string) =>
                fetchJson<void>(`/api/admin/article-media/${encodeURIComponent(key)}`, {
                    method: 'DELETE',
                }),
        },
    },
};
